import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function VerifyEmail() {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();

  const emailParam = searchParams.get("email") || "";
  const otpParam   = searchParams.get("otp")   || "";

  const [status, setStatus]   = useState("verifying"); // verifying | success | error | manual
  const [message, setMessage] = useState("");
  const [otp, setOtp]         = useState(otpParam);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMsg, setResendMsg]           = useState("");
  const [submitting, setSubmitting]         = useState(false);

  /* ── Auto-verify on mount when both params present ── */
  useEffect(() => {
    if (emailParam && otpParam) {
      verify(emailParam, otpParam);
    } else {
      setStatus("manual");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (email, code) => {
    setStatus("verifying");
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/verify-email`, { email, otp: code });
      localStorage.setItem("token", res.data.token);
      setStatus("success");
      setTimeout(() => navigate("/"), 2500);
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || "Invalid or expired code.";
      setMessage(msg);
      setStatus("error");
    }
  };

  const handleManualVerify = async () => {
    if (!otp || otp.length < 4) return;
    setSubmitting(true);
    await verify(emailParam, otp);
    setSubmitting(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !emailParam) return;
    setResendMsg("");
    try {
      await axios.post(`${API_BASE_URL}/auth/resend-otp`, { email: emailParam });
      setResendMsg("A new code has been sent to your email.");
      setResendCooldown(60);
      const t = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) { clearInterval(t); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch {
      setResendMsg("Failed to resend. Try again.");
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      background: "var(--bg-primary)",
    }}>
      <style>{`
        @keyframes verifySpin {
          to { transform: rotate(360deg); }
        }
        @keyframes verifyFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .verify-card {
          background: var(--bg-card);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          padding: 40px 36px;
          width: 100%;
          max-width: 420px;
          animation: verifyFadeIn 0.3s ease both;
          text-align: center;
        }
        .verify-spinner {
          width: 36px;
          height: 36px;
          border: 2.5px solid var(--border-default);
          border-top-color: var(--accent);
          border-radius: 50%;
          animation: verifySpin 0.7s linear infinite;
          margin: 0 auto 20px;
        }
        .verify-icon {
          font-size: 40px;
          margin-bottom: 16px;
          line-height: 1;
        }
        .verify-title {
          font-family: var(--font-serif);
          font-size: 24px;
          color: var(--text-logo);
          margin: 0 0 8px;
        }
        .verify-sub {
          font-size: 13px;
          color: var(--text-muted);
          line-height: 1.6;
          margin: 0 0 24px;
        }
        .verify-error-box {
          font-size: 12px;
          color: var(--danger-text);
          padding: 10px 12px;
          background: var(--danger-bg);
          border: 1px solid var(--danger-border);
          border-radius: var(--radius-sm);
          margin-bottom: 16px;
          text-align: left;
        }
        .verify-success-box {
          font-size: 12px;
          color: var(--success-text, #22c55e);
          padding: 10px 12px;
          background: var(--success-bg, rgba(34,197,94,0.08));
          border: 1px solid var(--success-border, rgba(34,197,94,0.25));
          border-radius: var(--radius-sm);
          margin-bottom: 16px;
          text-align: left;
        }
        .otp-input-lg {
          letter-spacing: 0.35em;
          font-size: 22px;
          text-align: center;
          font-family: var(--font-mono, monospace);
        }
        .verify-resend-link {
          background: none;
          border: none;
          font-size: 12px;
          color: var(--accent);
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .verify-resend-link:disabled {
          opacity: 0.5;
          cursor: default;
        }
      `}</style>

      <div className="verify-card">
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 28 }}>
          <span className="tc-dot" />
          <span style={{ fontFamily: "var(--font-serif)", fontSize: 18, color: "var(--text-logo)", letterSpacing: "0.01em" }}>
            TableCure
          </span>
        </div>

        {/* ── Verifying ── */}
        {status === "verifying" && (
          <>
            <div className="verify-spinner" />
            <h2 className="verify-title">Verifying…</h2>
            <p className="verify-sub">Confirming your email address, please wait.</p>
          </>
        )}

        {/* ── Success ── */}
        {status === "success" && (
          <>
            <div className="verify-icon">✓</div>
            <h2 className="verify-title">Email verified!</h2>
            <p className="verify-sub">
              Your account is now active. Redirecting you to the homepage…
            </p>
          </>
        )}

        {/* ── Error (OTP wrong/expired) — show manual entry ── */}
        {(status === "error" || status === "manual") && (
          <>
            <h2 className="verify-title">Verify your email</h2>
            <p className="verify-sub">
              {emailParam
                ? <>Enter the 6-digit code sent to <strong style={{ color: "var(--text-primary)" }}>{emailParam}</strong>.</>
                : "Enter the verification code sent to your email."}
            </p>

            {status === "error" && (
              <div className="verify-error-box">{message}</div>
            )}

            {resendMsg && (
              <div className="verify-success-box">{resendMsg}</div>
            )}

            <div className="tc-field" style={{ marginBottom: 16, textAlign: "left" }}>
              <label className="tc-label">Verification code</label>
              <input
                className="tc-input otp-input-lg"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="––––––"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                onKeyDown={e => e.key === "Enter" && handleManualVerify()}
                autoFocus
              />
            </div>

            <button
              className="tc-btn tc-btn-primary"
              style={{ width: "100%", padding: "12px", fontSize: "12px", justifyContent: "center", marginBottom: 16 }}
              onClick={handleManualVerify}
              disabled={submitting || otp.length < 4}
            >
              {submitting ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <span className="tc-spinner" style={{ width: 14, height: 14, borderWidth: 1.5 }} />
                  Verifying…
                </span>
              ) : "Verify & continue"}
            </button>

            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Didn't receive it?{" "}
              <button
                className="verify-resend-link"
                onClick={handleResend}
                disabled={resendCooldown > 0 || !emailParam}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
